// @flow

import * as React from 'react'

import styled from '@emotion/styled'
import { colors, media, variables } from '@toggl/style'
import { hooks } from '../../index'
import * as text from '../text'
import Links from './links'

export { Product, ProductsGroup } from './product'
export { default as MenuContent } from './menuContent'
export { default as ExpandedMenu } from './expandedMenu'
export { BackSocial } from './backSocial'
import { css } from '@emotion/core'

type Props = {
  Logo: React.ComponentType<any>,
  Cta: React.ComponentType<any>,
  children: React.Node,
  linksMinWidths: {| +tablet: string, +desktop: string |},
  login: React.Node,
  logoWidth?: [string, string],
  background?: string,
  setContentPadding?: (typeof undefined | number) => void,
  open: boolean,
  onToggle: (open: boolean) => void,
}

export const Header = ({
  Logo,
  children,
  Cta,
  linksMinWidths,
  login,
  logoWidth,
  background,
  setContentPadding,
  open,
  onToggle,
}: Props) => {
  const wrapperRef = React.useRef(null)
  const linksWrapperRef = React.useRef(null)

  const isDesktop = hooks.useMediaQuery(media.mqbp[1])
  const linksRef = React.useRef(null)

  const [mobileScreenHeight, setMobileScreenHeight] = React.useState(null)
  React.useLayoutEffect(() => {
    setContentPadding && setContentPadding(linksRef.current?.offsetLeft)
    if (!isDesktop) {
      setMobileScreenHeight(window.innerHeight)
    }
  }, [isDesktop, setContentPadding])

  const [mobileOpen, setMobileOpen] = React.useState(false)
  const isLogoDark = hooks.useSetDebounce(open)
  const debouncedSsMobileOpen = hooks.useSetDebounce(mobileOpen, true)

  return (
    <div ref={wrapperRef}>
      <HeaderLine background={background} open={open}>
        <LogoWrapper logoWidth={logoWidth}>
          <Logo dark={isLogoDark || !isDesktop} />
        </LogoWrapper>

        {isDesktop && (
          <>
            <RefDiv ref={linksRef}>
              <StyledLinks minWidths={linksMinWidths} open={open}>
                {children}
              </StyledLinks>
            </RefDiv>
            <CtaWrapper>
              <LoginWrapper open={open}>{login}</LoginWrapper>
              <CtaButtonWrapper>
                <Cta />
              </CtaButtonWrapper>
            </CtaWrapper>
          </>
        )}

        <LinksWrapper
          open={mobileOpen}
          ref={linksWrapperRef}
          mobileScreenHeight={mobileScreenHeight}
        >
          {!isDesktop && debouncedSsMobileOpen && (
            <MobileLinksWrapper>
              <Links minWidths={linksMinWidths}>{children}</Links>

              <CtaWrapper>
                <CtaButtonWrapper>
                  <Cta />
                </CtaButtonWrapper>
                <div>{login}</div>
              </CtaWrapper>
            </MobileLinksWrapper>
          )}
        </LinksWrapper>
        <Burger
          open={mobileOpen}
          onClick={() => {
            onToggle(!mobileOpen)
            setMobileOpen(!mobileOpen)
          }}
        >
          <div />
          <div />
          <div />
        </Burger>
      </HeaderLine>
    </div>
  )
}
const RefDiv = styled.div`
  ${media.mq[1]} {
    margin-right: 40px;

  ${media.mq[2]} {
    margin-left: 7%;
    margin-right: auto;
  }
`
const LoginWrapper = styled.div`
  margin-right: 20px;

  ${media.mq[2]} {
    margin-right: 30px;
  }

  button,
  a {
    color: ${(p) => (p.open ? colors.darkPurple : colors.almostWhite)};
  }
`

const MobileLinksWrapper = styled.div`
  padding: 30px 0;
  display: flex;
  flex-direction: column;
  height: 100%;
`

const StyledLinks = styled(Links)`
  height: 24px;
  li {
    a {
      text-decoration: none;
      color: ${(p) => (p.open ? colors.darkPurple : colors.almostWhite)};
    }
    ${media.mq[1]} {
      ${text.heading5};
      color: ${(p) => (p.open ? colors.darkPurple : colors.almostWhite)};
    }
  }
`

const CtaButtonWrapper = styled.div`
  margin-bottom: 20px;

  ${media.mq[1]} {
    margin: 0;
  }
`

const LogoWrapper = styled.div`
  width: ${(p) => (p.logoWidth && p.logoWidth[0] ? p.logoWidth[0] : '64px')};
  display: flex;
  flex-shrink: 0;
  margin-right: 30px;
  ${media.mq[2]} {
    width: ${(p) => (p.logoWidth && p.logoWidth[1] ? p.logoWidth[1] : '85px')};
  }
  svg {
    width: 100%;
  }
`

const openAnimateCss = css`
  transition: background-color 200ms linear;
  transition-delay: 300ms;
`

const HeaderLine: React.ComponentType<any> = styled.div`
  background-color: ${(p) => p.theme.background};
  border-bottom: solid 1px ${(p) => p.theme.borderColor};
  padding: 0 20px;
  height: ${variables.headerHeight}px;
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${(p) => (!p.open ? openAnimateCss : '')}

  ${media.mq[1]} {
    height: ${variables.headerHeightDesktop}px;
    border-bottom: none;
    background-color: ${({ open, theme, background }) =>
      open ? theme.backgroundDesktop : background || 'transparent'};
  }
`

const Burger: React.ComponentType<any> = styled.button`
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  width: 24px;
  height: 18px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  z-index: 10;

  &:focus {
    outline: none;
  }

  ${media.mq[1]} {
    display: none;
  }

  div {
    width: 24px;
    height: 2px;
    background: ${colors.darkPurple};
    border-radius: 10px;
    position: relative;
    transform-origin: 3px;

    :first-of-type {
      transform: ${({ open }) => (open ? 'rotate(45deg)' : 'rotate(0)')};
    }

    :nth-of-type(2) {
      opacity: ${({ open }) => (open ? '0' : '1')};
      transform: ${({ open }) => (open ? 'translateX(20px)' : 'translateX(0)')};
    }

    :nth-of-type(3) {
      transform: ${({ open }) => (open ? 'rotate(-45deg)' : 'rotate(0)')};
    }
  }
`

const CtaWrapper: React.ComponentType<any> = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: auto;

  ${media.mq[1]} {
    flex-direction: row;
    justify-content: space-between;
    margin-top: 0;
    margin-left: auto;
  }
`

const LinksWrapper: React.ComponentType<any> = styled.div`
  position: absolute;
  top: ${variables.headerHeight}px;
  z-index: 20;
  margin: 0 -20px;
  width: 100%;
  background: ${(p) => p.theme.background};
  padding: 0 20px;
  transition: all 300ms linear;
  ${text.heading4};
  height: 0;
  height: ${(p) =>
    p.open ? `${p.mobileScreenHeight - variables.headerHeight}px` : '0;'};
  max-height: ${(p) =>
    p.open ? `${p.mobileScreenHeight - variables.headerHeight}px` : `0`};
  display: flex;
  flex-direction: column;

  ul > li {
    margin-bottom: 25px;

    a,
    button,
    button:hover {
      text-decoration: none;
      color: ${colors.fadedPurple};
    }
  }

  ${media.mq[1]} {
    top: ${variables.headerHeightDesktop}px;
    height: auto;
    padding: 0 20px;
    overflow: hidden;
  }
`
